<docs>
  Floorplan
</docs>

<template>
  <section class="floorplan">
    <div
      class="floorplan-container"
      :class="{
        'floorplan-dragging': isDragging,
        'floorplan-overflow-hidden': isDraggable || isResizable
      }"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @mousedown="handleDragStart"
      @mousewheel="handleScroll"
      @DOMMouseScroll="handleScroll"
    >
      <span
        class="floorplan-inner"
        :style="{
          transform: `translate(${interactX * floorplanScale}px, ${interactY * floorplanScale}px) scale(${floorplanScale})`
        }"
      >
        <icon
          class="floorplan-image"
          :has-lazyload="true"
          :image="floorplan"
          :style="{
            width: `${floorplanContainerWidth}px`,
            height: `${floorplanContainerHeight}px`,
          }"
        >
          <floorplan-dots
            :ratio="floorplanContainerScale"
            :ratio-rfe="floorplanContainerScaleRfe"
            :x-offset="xOffset"
            :y-offset="yOffset"
            :activated-color="activatedColor"
            :scale="scaleOfDotsAndRange"
            :floorplan-scale="floorplanScale"
          />
          <floorplan-range
            :ratio="floorplanContainerScale"
            :ratio-rfe="floorplanContainerScaleRfe"
            :x-offset="xOffset"
            :y-offset="yOffset"
            :scale="scaleOfDotsAndRange"
          />
        </icon>
      </span>
      <i-repeat
        v-if="isResizable"
        class="floorplan-zoom-in-container"
        li-class="floorplan-zoom-in-item"
        :items="zoomInButtons"
      >
        <template #default="{ item: button }">
          <icon
            :class="button.className"
            @click.native="button.method"
          />
        </template>
      </i-repeat>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import Icon from '../../Icon/index.vue'
import IRepeat from '../../IRepeat.vue'
import FloorplanDots from './FloorplanDots.vue'
import FloorplanRange from './FloorplanRange.vue'
import HandleData from '../../../mixins/HandleData.vue'

export default {
  name: 'FloorplanIndex',
  components: {
    FloorplanRange,
    FloorplanDots,
    Icon,
    IRepeat
  },

  mixins: [
    HandleData
  ],

  props: {
    floorplanContainerWidth: {
      type: Number,
      default () {
        return 200
      }
    },
    floorplanContainerHeight: {
      type: Number,
      default () {
        return 200
      }
    },
    activatedColor: {
      type: String,
      default () {
        return ''
      }
    },
    isResizable: {
      type: Boolean,
      default () {
        return false
      }
    },
    isDraggable: {
      type: Boolean,
      default () {
        return false
      }
    }
  },

  data () {
    return {
      floorplanHeight: this.floorplanContainerHeight,
      floorplanWidth: this.floorplanContainerWidth,
      floorplanScale: 1,
      floorplanScaleMax: 16,
      floorplanScaleMin: 1,
      interactX: 0,
      interactY: 0,
      isDragging: false,
      lastX: 0,
      lastY: 0,
      xOffset: 0,
      yOffset: 0,
      floorplanImage: null,
      scaleOfDotsAndRangeMax: 1,
      scaleOfDotsAndRangeMin: 0.5,
      zoomInButtons: [{
        name: 'zoom-in',
        className: 'icon-floorplan-add',
        method: () => {
          this.setFloorplanScale(2)
        }
      }, {
        name: 'zoom-out',
        className: 'icon-floorplan-minus',
        method: () => {
          this.setFloorplanScale(0.5)
        }
      }]
    }
  },

  computed: {
    ...mapGetters([
      'currentPanorama',
      'floorplan',
      'panoramas'
    ]),

    floorplanDotsMinDistance () {
      const ratio = this.floorplanContainerScale
      const ratioRfe = this.floorplanContainerScaleRfe
      const x = []
      const y = []
      for (let i = 0; i < this.panoramas.length; i++) {
        x.push((this.panoramas[i].position ? this.panoramas[i].position.x + 12.85 / ratioRfe : 0) * ratio + this.xOffset - 10)
        y.push((this.panoramas[i].position ? this.panoramas[i].position.y + 15.00 / ratioRfe : 0) * ratio + this.yOffset - 10)
      }
      let minDis = 10000
      for (let i = 0; i < this.panoramas.length; i++) {
        for (let j = i + 1; j < this.panoramas.length; j++) {
          const dis = Math.sqrt(Math.pow(x[i] - x[j], 2) + Math.pow(y[i] - y[j], 2))
          minDis = Math.min(dis, minDis)
        }
      }
      return minDis
    },

    floorplanContainerScale () {
      // Come from vreditor > MarkerInfo.vue
      // Floor plan scale to fit container
      const containerHeight = this.floorplanContainerHeight
      const containerWidth = this.floorplanContainerWidth
      const scaleHeight = containerHeight / this.floorplanHeight || 1
      const scaleWidth = containerWidth / this.floorplanWidth || 1
      return Math.min(scaleHeight, scaleWidth)
    },

    floorplanContainerScaleRfe () {
      // Come from vreditor > MarkerInfo.vue
      // Floor plan scale to fit container
      const containerHeight = 450
      const containerWidth = 600
      const scaleHeight = containerHeight / this.floorplanHeight || 1
      const scaleWidth = containerWidth / this.floorplanWidth || 1
      return Math.min(scaleHeight, scaleWidth, 1)
    },

    scaleOfDotsAndRange () {
      // now the floorplan-point is 20px
      let scale = this.floorplanDotsMinDistance / 20
      scale = Math.min(scale, this.scaleOfDotsAndRangeMax)
      scale = Math.max(scale, this.scaleOfDotsAndRangeMin / this.floorplanScale)
      return scale
    }
  },

  mounted () {
    if (this.isDraggable) {
      window.addEventListener('mousemove', this.handleDragging)
      window.addEventListener('mouseup', this.handleDragStop)
    }
    if (this.floorplan) {
      this.floorplanImage = new Image() // eslint-disable-line
      this.floorplanImage.src = this.floorplan
      this.floorplanImage.onload = () => {
        this.interactX = 0
        this.interactY = 0
        this.floorplanScale = 1
        this.floorplanWidth = this.floorplanImage.width
        this.floorplanHeight = this.floorplanImage.height
        const ratio = this.floorplanContainerScale
        if (this.floorplanWidth < this.floorplanHeight) {
          this.xOffset = (this.floorplanContainerWidth - this.floorplanWidth * ratio) / 2
          this.yOffset = 0
        } else if (this.floorplanWidth > this.floorplanHeight) {
          this.xOffset = 0
          this.yOffset = (this.floorplanContainerHeight - this.floorplanHeight * ratio) / 2
        } else {
          this.xOffset = 0
          this.yOffset = 0
        }
      }
    }
  },

  updated () {
    if (this.floorplan && this.floorplanImage.src !== this.floorplan) {
      this.floorplanImage.src = this.floorplan
    }
  },

  beforeDestroy () {
    if (this.isDraggable) {
      window.removeEventListener('mousemove', this.handleDragging)
      window.removeEventListener('mouseup', this.handleDragStop)
    }
  },

  methods: {
    setFloorplanScale (n = 1) {
      if ((this.floorplanScale * n) > this.floorplanScaleMax) {
        this.floorplanScale = this.floorplanScaleMax
      } else if ((this.floorplanScale * n) < this.floorplanScaleMin) {
        this.floorplanScale = this.floorplanScaleMin
      } else {
        this.floorplanScale *= n
      }
    },

    handleScroll (e = null) {
      if (this.isResizable) {
        e.preventDefault()
        e.stopPropagation()
        const ratio = 0.1
        const n = (e.wheelDelta ? e.wheelDelta : -e.detail) > 0 ? ratio : -ratio
        this.setFloorplanScale(1 + n)
      }
    },

    handleTouchStart (e = null) {
      if (!this.isDraggable || !e) {
        return
      }
      this.lastX = e.touches[0].pageX
      this.lastY = e.touches[0].pageY
      this.isDragging = true
    },

    handleTouchMove (e = null) {
      if (!this.isDraggable || !e) {
        return
      }
      if (this.isDragging) {
        this.interactX += (e.touches[0].pageX - this.lastX) / this.floorplanScale
        this.interactY += (e.touches[0].pageY - this.lastY) / this.floorplanScale
        this.lastX = e.touches[0].pageX
        this.lastY = e.touches[0].pageY
        ;[this.interactX, this.interactY] = limitPosition(this.floorplanContainerWidth / 2, this.floorplanContainerHeight / 2, this.interactX, this.interactY)
      }
    },

    handleTouchEnd () {
      this.isDragging = false
    },

    handleDragStart (e = null) {
      if (this.isDraggable) {
        this.lastX = e.pageX
        this.lastY = e.pageY
        this.isDragging = true
      }
    },

    handleDragging (e = null) {
      if (this.isDragging) {
        this.cancelSelection()
        this.interactX += (e.pageX - this.lastX) / this.floorplanScale
        this.interactY += (e.pageY - this.lastY) / this.floorplanScale
        this.lastX = e.pageX
        this.lastY = e.pageY
        ;[this.interactX, this.interactY] = limitPosition(this.floorplanContainerWidth / 2, this.floorplanContainerHeight / 2, this.interactX, this.interactY)
      }
    },

    cancelSelection () {
      if (window.getSelection) {
        if (window.getSelection().empty) { // Chrome
          window.getSelection().empty()
        } else if (window.getSelection().removeAllRanges) { // Firefox
          window.getSelection().removeAllRanges()
        }
      } else if (document.selection) { // IE?
        document.selection.empty()
      }
    },

    handleDragStop () {
      this.isDragging = false
    }
  }
}

function limitPosition (limitW = 0, limitH = 0, x = 0, y = 0) {
  if (x < -limitW) {
    x = -limitW
  }
  if (y < -limitH) {
    y = -limitH
  }
  if (x > limitW) {
    x = limitW
  }
  if (y > limitH) {
    y = limitH
  }
  return [x, y]
}
</script>

<style lang="stylus" rel="stylesheet/stylus" scoped>
@import '~css/addon/main'
@import '~css/common/extends'

.floorplan {
  @extend .full
  @extend .flex-center
  position: relative
  background-color: alpha($dark-gray, 80%)
}

.floorplan-image {
  @extend .bg-center
  position: relative
  background-size: contain
  transition: transform .1s ease-out
}

.floorplan-container {
  @extend .flex-center
  position: relative
  background-color: $white

  &.floorplan-overflow-hidden {
    overflow: hidden
  }
}

.floorplan-dragging {
  cursor: move
}

.floorplan-inner {
  @extend .flex-center
  perspective: 225px
}

.floorplan-zoom-in-container {
  position: absolute
  right: $floorplan-floorplan-zoom-in-container-pos
  bottom: 0
  margin-bottom: 10px
  margin-right: $floorplan-floorplan-zoom-in-container-margin
  border-width: .5px
  border-style: solid
  border-color: alpha(#979797, 80%)
  background-color: alpha($white, 80%)

  .icon {
    @extend .full
    background-size: 20px 20px
  }
}

>>> .floorplan-zoom-in-item {
  @extend .flex-center-column
  position: relative
  width: 30px
  height: 30px
  cursor: pointer

  & + & {
    &::before {
      content: ''
      position: absolute
      top: 0
      left: 4px
      right: 4px
      height: 0.5px
      background-color: alpha(#979797, 80%)
    }
  }
}
</style>

<style lang="stylus" rel="stylesheet/stylus">
@import '~css/addon/main'

.theme-rtl.theme-rtl-overlap {
  // floorplan no need to be rtl
  .floorplan-container {
    direction: ltr
  }

  .floorplan-zoom-in-container {
    right: auto
    left: $floorplan-floorplan-zoom-in-container-pos
    margin-right: auto
    margin-left: $floorplan-floorplan-zoom-in-container-margin
  }
}
</style>
